import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import axios from "axios";
import { 
  Menu, X, Phone, Mail, MapPin, ChevronRight, ArrowRight, 
  Globe, Shield, Truck, Users, CheckCircle, Building2,
  Package, Zap, Sofa, Sun, Car, Wrench, ShoppingCart,
  UtensilsCrossed, Upload, FileText, Send, ExternalLink,
  Award, Target, Eye, Briefcase, Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component - PNG with transparent background
const Logo = "https://customer-assets.emergentagent.com/job_habibi-builder/artifacts/8019ekm1_Market%20Plus%20Logo_Final_Green.png";

// Landline number
const LANDLINE_NUMBER = "+9714 513 5252";

// Contact email
const CONTACT_EMAIL = "contact@marketplus.ae";

// WhatsApp number
const WHATSAPP_NUMBER = "+971522456617";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Divisions", path: "/divisions" },
    { name: "Partner With Us", path: "/partner" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-white/95 backdrop-blur-md shadow-sm py-3" 
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={Logo} 
                alt="Market Plus" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`nav-link text-sm font-medium tracking-wide transition-colors ${
                    isScrolled ? "text-gray-800 hover:text-[#043927]" : "text-white hover:text-[#D4AF37]"
                  } ${location.pathname === link.path ? "active" : ""}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/partner"
                data-testid="nav-cta-partner"
                className="bg-[#D4AF37] text-[#01150E] px-6 py-3 text-sm font-semibold hover:bg-[#D4AF37]/90 transition-all btn-shimmer"
              >
                Partner With Us
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={isScrolled ? "text-gray-800" : "text-white"} size={24} />
              ) : (
                <Menu className={isScrolled ? "text-gray-800" : "text-white"} size={24} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#043927] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="pt-24 px-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white text-2xl font-medium hover:text-[#D4AF37] transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/partner"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[#D4AF37] text-[#01150E] px-6 py-4 text-lg font-semibold text-center mt-4"
              >
                Partner With Us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// WhatsApp Button Component
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-button"
    className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform whatsapp-pulse"
  >
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Footer Component
const Footer = () => (
  <footer className="bg-[#01150E] text-white footer-pattern" data-testid="footer">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Company Info */}
        <div className="lg:col-span-2">
          <img src={Logo} alt="Market Plus" className="h-16 w-auto mb-6 bg-white p-2 rounded" />
          <p className="text-gray-400 text-lg leading-relaxed mb-6 max-w-md">
            Your Gateway to UAE Trade, Distribution & Market Growth. Connecting global manufacturers to Middle East markets.
          </p>
          <div className="flex gap-4">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-[#043927] rounded flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-lg font-semibold mb-6 text-[#D4AF37]">Quick Links</h4>
          <ul className="space-y-4">
            {["Home", "About Us", "Divisions", "Partner With Us", "Contact"].map((link) => (
              <li key={link}>
                <Link
                  to={link === "Home" ? "/" : `/${link.toLowerCase().replace(/\s/g, '-').replace('us', '')}`}
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2"
                >
                  <ChevronRight size={14} />
                  {link}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h4 className="text-lg font-semibold mb-6 text-[#D4AF37]">Contact Us</h4>
          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-gray-400 hover:text-[#D4AF37]">
                {CONTACT_EMAIL}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
              <a href={`tel:${LANDLINE_NUMBER.replace(/\s/g, '')}`} className="text-gray-400 hover:text-[#D4AF37]">
                {LANDLINE_NUMBER}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
              <a href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D4AF37]">
                {WHATSAPP_NUMBER} (WhatsApp)
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} Market Plus General Trading. All rights reserved.
        </p>
        <p className="text-gray-500 text-sm">
          DED Licensed Company | Dubai, UAE
        </p>
      </div>
    </div>
  </footer>
);

// Section Wrapper with Animation
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Home Page
const HomePage = () => {
  const divisions = [
    { icon: UtensilsCrossed, title: "Food & FMCG", desc: "Spices, grains, packaged foods, beverages" },
    { icon: Zap, title: "Electrical & Electronics", desc: "Appliances, smart devices, components" },
    { icon: Building2, title: "Industrial & Automation", desc: "Machinery, sensors, control systems" },
    { icon: Sofa, title: "Furniture & Interiors", desc: "Lab, office, and residential solutions" },
    { icon: Sun, title: "Renewable Energy", desc: "Solar panels, energy storage systems" },
    { icon: Car, title: "Automotive & Accessories", desc: "Parts, lubricants, tools, accessories" },
    { icon: Wrench, title: "Building Materials", desc: "Construction tools, fittings, hardware" },
    { icon: ShoppingCart, title: "E-Commerce & Retail", desc: "Amazon, Carrefour, Noon partnerships" },
  ];

  const whyChooseUs = [
    { icon: Globe, title: "End-to-End Solutions", desc: "Complete trade solutions from sourcing to delivery" },
    { icon: Shield, title: "Compliance Support", desc: "Licensing & regulatory approval assistance" },
    { icon: ShoppingCart, title: "Retail Access", desc: "Direct placement in major retail networks" },
    { icon: Package, title: "Global Sourcing", desc: "Established network across India, China & Europe" },
    { icon: Users, title: "Multi-Industry Expertise", desc: "Diverse portfolio spanning 8+ sectors" },
    { icon: Truck, title: "Reliable Logistics", desc: "Efficient warehousing and delivery systems" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2000&q=80)` 
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-6">
              Welcome to Market Plus
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Partner With Us — Your Gateway to{" "}
              <span className="text-[#D4AF37]">UAE Trade</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 leading-relaxed max-w-2xl">
              From food to furniture, from electronics to energy — Market Plus connects global manufacturers and brands to the UAE & GCC through end-to-end import, compliance, distribution, and retail partnerships.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/partner"
                data-testid="hero-cta-partner"
                className="bg-[#D4AF37] text-[#01150E] px-8 py-4 text-lg font-semibold hover:bg-[#D4AF37]/90 transition-all inline-flex items-center justify-center gap-2 btn-shimmer"
              >
                Partner With Us
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/partner#catalogue"
                data-testid="hero-cta-catalogue"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold hover:bg-white hover:text-[#043927] transition-all inline-flex items-center justify-center gap-2"
              >
                Send Product Catalogue
              </Link>
            </div>

            {/* Trust Points */}
            <div className="flex flex-wrap gap-6 md:gap-10">
              {[
                "Based in Dubai",
                "DED Mainland License",
                "4+ Years Operations",
                "Global Sourcing"
              ].map((point, idx) => (
                <div key={idx} className="trust-badge flex items-center gap-2 text-white/90">
                  <CheckCircle size={18} className="text-[#D4AF37]" />
                  <span className="text-sm font-medium">{point}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Snapshot */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                About Us
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6 leading-tight">
                A Full-Spectrum Trading & Distribution Powerhouse
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Market Plus General Trading is a diversified trading and distribution company headquartered in Dubai, UAE. We specialize in bringing global brands into the UAE market—handling everything from product registration and compliance to distribution, e-commerce placement, and retail partnerships.
              </p>
              <Link
                to="/about"
                data-testid="about-cta"
                className="inline-flex items-center gap-2 text-[#043927] font-semibold hover:text-[#D4AF37] transition-colors"
              >
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Warehouse operations"
                className="w-full h-[400px] object-cover shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#043927] text-white p-8 shadow-xl">
                <div className="text-4xl font-bold text-[#D4AF37] mb-2">4+</div>
                <div className="text-sm uppercase tracking-wider">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Divisions Preview */}
      <AnimatedSection className="py-24 md:py-32 bg-[#FAFAF9]" data-testid="divisions-preview">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Our Divisions
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6">
              One Company. Multiple Industries.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Market Plus operates across diverse sectors, providing comprehensive trading and distribution solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {divisions.map((div, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-white p-8 border border-gray-100 hover:bg-[#043927] transition-all duration-500 card-hover cursor-pointer"
              >
                <div className="w-14 h-14 bg-[#043927]/10 group-hover:bg-[#D4AF37] rounded flex items-center justify-center mb-6 transition-colors">
                  <div.icon size={28} className="text-[#043927] group-hover:text-[#01150E]" />
                </div>
                <h3 className="text-xl font-semibold text-[#043927] group-hover:text-white mb-3 transition-colors">
                  {div.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors">
                  {div.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/divisions"
              data-testid="divisions-cta"
              className="inline-flex items-center gap-2 bg-[#043927] text-white px-8 py-4 font-semibold hover:bg-[#01150E] transition-colors"
            >
              Explore All Divisions
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Why Market Plus
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6 leading-tight">
                Your Trusted Partner for Middle East Expansion
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We combine deep market knowledge, established networks, and comprehensive service capabilities to help brands succeed in the GCC region.
              </p>
              <Link
                to="/partner"
                data-testid="why-us-cta"
                className="bg-[#D4AF37] text-[#01150E] px-8 py-4 text-lg font-semibold hover:bg-[#D4AF37]/90 transition-all inline-flex items-center gap-2"
              >
                Start Partnership
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {whyChooseUs.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-12 h-12 bg-[#043927]/10 rounded flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-[#043927]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#043927] mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Global Reach Section */}
      <AnimatedSection className="py-24 md:py-32 bg-[#043927] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Global Network
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                Connecting Markets Worldwide
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                We source globally from India, China, and Europe and distribute across UAE and GCC markets including KSA and Oman. Our established network ensures efficient supply chain management and timely delivery.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">3+</div>
                  <div className="text-gray-400">Source Regions</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">5+</div>
                  <div className="text-gray-400">Distribution Markets</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">8+</div>
                  <div className="text-gray-400">Industry Sectors</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#D4AF37] mb-2">100+</div>
                  <div className="text-gray-400">Partner Brands</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=800&q=80"
                alt="Global logistics"
                className="w-full h-[400px] object-cover opacity-80"
              />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Final CTA */}
      <AnimatedSection className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6">
            Ready to Expand Across the Middle East?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            Partner with Market Plus and unlock access to UAE & GCC markets through our comprehensive trade ecosystem.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/partner"
              data-testid="final-cta-partner"
              className="bg-[#043927] text-white px-10 py-5 text-lg font-semibold hover:bg-[#01150E] transition-all inline-flex items-center justify-center gap-2"
            >
              Partner With Us
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/partner#catalogue"
              data-testid="final-cta-catalogue"
              className="border-2 border-[#043927] text-[#043927] px-10 py-5 text-lg font-semibold hover:bg-[#043927] hover:text-white transition-all inline-flex items-center justify-center gap-2"
            >
              Send Product Catalogue
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const services = [
    "Import & Export",
    "Distribution & Dealership",
    "Product Sourcing & Procurement",
    "Brand Setup & Market Entry",
    "Municipality & Compliance Approvals",
    "White/Private Label Solutions",
    "Logistics, Warehousing & Fulfillment",
    "E-Commerce & Retail Placement"
  ];

  const strengths = [
    { icon: Building2, text: "Dubai-based mainland company (DED license)" },
    { icon: Globe, text: "Global sourcing network" },
    { icon: Package, text: "Multi-industry operations" },
    { icon: ShoppingCart, text: "Strong retail & e-commerce partnerships" },
    { icon: CheckCircle, text: "End-to-end execution capability" }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-[#043927]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              About Us
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 max-w-3xl">
              Building Bridges Between Global Markets
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Discover the story behind Market Plus and our commitment to excellence in trading and distribution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6">
                Market Plus General Trading
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Market Plus General Trading is a diversified trading and distribution company headquartered in Dubai, United Arab Emirates. We bridge international manufacturers and regional markets—helping global brands establish, expand, and thrive across the UAE and the wider Middle East.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                What began as a focused trading initiative over three years ago has grown into a multi-vertical enterprise. Starting with food and FMCG imports from India, Market Plus expanded into electronics, industrial automation, furniture, and renewable energy.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/6950015/pexels-photo-6950015.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Corporate meeting"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* What We Do */}
      <AnimatedSection className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Our Services
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-6">
              What We Do
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-6 border border-gray-100 hover:border-[#D4AF37] transition-colors"
              >
                <div className="w-10 h-10 bg-[#043927]/10 rounded flex items-center justify-center mb-4">
                  <Briefcase size={20} className="text-[#043927]" />
                </div>
                <h3 className="font-semibold text-[#043927]">{service}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Mission & Vision */}
      <AnimatedSection className="py-24 md:py-32 bg-[#043927] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="border-l-4 border-[#D4AF37] pl-8">
              <div className="flex items-center gap-3 mb-4">
                <Target size={28} className="text-[#D4AF37]" />
                <h3 className="text-2xl font-bold">Our Mission</h3>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed">
                To connect global markets through trusted partnerships, efficient trade networks, and world-class distribution channels.
              </p>
            </div>
            <div className="border-l-4 border-[#D4AF37] pl-8">
              <div className="flex items-center gap-3 mb-4">
                <Eye size={28} className="text-[#D4AF37]" />
                <h3 className="text-2xl font-bold">Our Vision</h3>
              </div>
              <p className="text-lg text-gray-300 leading-relaxed">
                To become the most trusted and diversified trading company in the Middle East.
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Strengths */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Our Strengths
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#043927] mb-8">
                What Sets Us Apart
              </h2>
              <div className="space-y-6">
                {strengths.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#043927] rounded flex items-center justify-center flex-shrink-0">
                      <item.icon size={24} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-lg text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/12047372/pexels-photo-12047372.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Logistics operations"
                className="w-full h-[500px] object-cover shadow-2xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#D4AF37] text-[#01150E] p-8 shadow-xl">
                <Award size={40} className="mb-2" />
                <div className="text-sm uppercase tracking-wider font-semibold">DED Licensed</div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="py-24 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#043927] mb-6">
            Ready to Partner With Us?
          </h2>
          <Link
            to="/partner"
            className="bg-[#043927] text-white px-10 py-5 text-lg font-semibold hover:bg-[#01150E] transition-all inline-flex items-center gap-2"
          >
            Start Partnership
            <ArrowRight size={20} />
          </Link>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Divisions Page
const DivisionsPage = () => {
  const divisions = [
    {
      icon: UtensilsCrossed,
      title: "Food & FMCG",
      desc: "Spices, grains, pulses, packaged foods, beverages, condiments. Strong expertise in Dubai Municipality approvals and retail distribution.",
      color: "bg-orange-50",
      iconColor: "text-orange-600",
      image: "https://images.pexels.com/photos/1366594/pexels-photo-1366594.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
      icon: Zap,
      title: "Electrical & Electronics",
      desc: "Home appliances, accessories, smart devices, industrial electrical components sourced from China and Europe.",
      color: "bg-blue-50",
      iconColor: "text-blue-600",
      image: "https://images.unsplash.com/photo-1740803292814-13d2e35924c3?w=800&auto=format&fit=crop"
    },
    {
      icon: Building2,
      title: "Industrial & Automation",
      desc: "Machinery, sensors, control systems, parking and vertical automation solutions.",
      color: "bg-purple-50",
      iconColor: "text-purple-600",
      image: "https://images.unsplash.com/photo-1767281075989-7571356d477e?w=800&auto=format&fit=crop"
    },
    {
      icon: Sofa,
      title: "Furniture & Interiors",
      desc: "Laboratory furniture, modular systems, office and residential furniture solutions.",
      color: "bg-amber-50",
      iconColor: "text-amber-600",
      image: "https://images.unsplash.com/photo-1758630737900-a28682c5aa69?w=800&auto=format&fit=crop"
    },
    {
      icon: Sun,
      title: "Renewable Energy",
      desc: "Solar panels, energy storage, green infrastructure components supporting sustainability goals.",
      color: "bg-green-50",
      iconColor: "text-green-600",
      image: "https://images.pexels.com/photos/9799706/pexels-photo-9799706.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
      icon: Car,
      title: "Automotive & Accessories",
      desc: "Parts, lubricants, tools, accessories supplied to workshops, distributors, and retailers.",
      color: "bg-red-50",
      iconColor: "text-red-600",
      image: "https://images.pexels.com/photos/3989850/pexels-photo-3989850.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
      icon: Wrench,
      title: "Building Materials & Hardware",
      desc: "Construction tools, fittings, fasteners, fixtures, and hardware for infrastructure projects.",
      color: "bg-slate-100",
      iconColor: "text-slate-600",
      image: "https://images.pexels.com/photos/4792486/pexels-photo-4792486.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
      icon: ShoppingCart,
      title: "E-Commerce & Retail Partnerships",
      desc: "Amazon, Carrefour, Noon, Sharaf DG, supermarkets and regional retail networks.",
      color: "bg-teal-50",
      iconColor: "text-teal-600",
      image: "https://images.unsplash.com/photo-1707328739134-7cf382e74fc2?w=800&auto=format&fit=crop"
    }
  ];

  return (
    <div data-testid="divisions-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-[#043927]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Our Divisions
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 max-w-4xl">
              One Company. Multiple Industries. Endless Possibilities.
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Market Plus operates across multiple sectors, connecting manufacturers, distributors, and retailers under one global network.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Divisions Grid */}
      <AnimatedSection className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {divisions.map((div, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border border-gray-100 hover:border-[#D4AF37] transition-all card-hover group overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={div.image} 
                    alt={div.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className={`w-14 h-14 ${div.color} rounded-lg flex items-center justify-center mb-4 -mt-12 relative z-10 shadow-lg`}>
                    <div.icon size={28} className={div.iconColor} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#043927] mb-3 group-hover:text-[#D4AF37] transition-colors">
                    {div.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {div.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Strategic Sourcing */}
      <AnimatedSection className="py-24 md:py-32 bg-[#043927] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
            Strategic Sourcing
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            Global Sourcing, Regional Distribution
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            We source from India, China, and Europe with regional distribution across UAE & GCC markets, ensuring quality products reach the right markets efficiently.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {["India", "China", "Europe", "UAE", "KSA", "Oman"].map((region, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Globe size={20} className="text-[#D4AF37]" />
                <span className="text-lg font-medium">{region}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#043927] mb-6">
            Looking for a Distribution Partner?
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Whether you're in food, electronics, or renewable energy, we have the expertise to help you enter the UAE market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/partner"
              className="bg-[#043927] text-white px-10 py-5 text-lg font-semibold hover:bg-[#01150E] transition-all inline-flex items-center justify-center gap-2"
            >
              Partner With Us
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-[#043927] text-[#043927] px-10 py-5 text-lg font-semibold hover:bg-[#043927] hover:text-white transition-all inline-flex items-center justify-center gap-2"
            >
              Contact Our Team
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Partner Page
const PartnerPage = () => {
  const [formType, setFormType] = useState("partner");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    const formData = new FormData(e.target);
    if (selectedFile) {
      formData.append("catalogue", selectedFile);
    }

    try {
      await axios.post(`${API}/partner-inquiry`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSubmitSuccess(true);
      e.target.reset();
      setSelectedFile(null);
    } catch (error) {
      setSubmitError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCatalogueSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    const formData = new FormData(e.target);
    if (selectedFile) {
      formData.append("catalogue", selectedFile);
    }

    try {
      await axios.post(`${API}/catalogue-submission`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSubmitSuccess(true);
      e.target.reset();
      setSelectedFile(null);
    } catch (error) {
      setSubmitError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const whoShouldPartner = [
    "Manufacturers",
    "Exporters",
    "Brand owners",
    "Distributors",
    "Product companies"
  ];

  const whatWeOffer = [
    "UAE & GCC market entry",
    "Compliance & approvals",
    "Import/export handling",
    "Distribution & retail placement",
    "E-commerce onboarding",
    "Long-term partnership model"
  ];

  const process = [
    { step: "01", title: "Submit Inquiry", desc: "Share your product details and catalogue" },
    { step: "02", title: "Evaluation", desc: "Our team reviews your submission" },
    { step: "03", title: "Onboarding", desc: "Strategy planning and partnership setup" },
    { step: "04", title: "Market Launch", desc: "Distribution and market entry execution" }
  ];

  return (
    <div data-testid="partner-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-[#043927]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Partnership
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 max-w-3xl">
              Partner With Market Plus
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Join our network and expand your brand across the UAE and GCC markets with our comprehensive partnership solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who Should Partner & What We Offer */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Who Should Partner
              </span>
              <h2 className="text-3xl font-bold text-[#043927] mb-8">
                Ideal Partners
              </h2>
              <div className="space-y-4">
                {whoShouldPartner.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#D4AF37]" />
                    <span className="text-lg text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                What We Offer
              </span>
              <h2 className="text-3xl font-bold text-[#043927] mb-8">
                Our Solutions
              </h2>
              <div className="space-y-4">
                {whatWeOffer.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <ArrowRight size={20} className="text-[#043927]" />
                    <span className="text-lg text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Process */}
      <AnimatedSection className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#043927]">
              How Partnership Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-[#D4AF37] mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-[#043927] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Form Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white" id="catalogue">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                Get Started
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#043927] mb-4">
                Submit Your Inquiry
              </h2>
              <p className="text-gray-600">
                Choose the type of inquiry and fill in your details
              </p>
            </div>

            {/* Form Type Toggle */}
            <div className="flex justify-center mb-10">
              <div className="bg-[#FAFAF9] p-1 rounded inline-flex">
                <button
                  data-testid="toggle-partner-form"
                  onClick={() => { setFormType("partner"); setSubmitSuccess(false); }}
                  className={`px-6 py-3 font-medium transition-all ${
                    formType === "partner" ? "bg-[#043927] text-white" : "text-gray-600 hover:text-[#043927]"
                  }`}
                >
                  Partner Inquiry
                </button>
                <button
                  data-testid="toggle-catalogue-form"
                  onClick={() => { setFormType("catalogue"); setSubmitSuccess(false); }}
                  className={`px-6 py-3 font-medium transition-all ${
                    formType === "catalogue" ? "bg-[#043927] text-white" : "text-gray-600 hover:text-[#043927]"
                  }`}
                >
                  Send Catalogue
                </button>
              </div>
            </div>

            {submitSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16 bg-[#043927] text-white form-success"
              >
                <CheckCircle size={64} className="mx-auto mb-6 text-[#D4AF37]" />
                <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                <p className="text-gray-300 mb-8">
                  Your {formType === "partner" ? "partnership inquiry" : "catalogue"} has been submitted successfully. Our team will review and contact you soon.
                </p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="bg-[#D4AF37] text-[#01150E] px-8 py-3 font-semibold hover:bg-[#D4AF37]/90 transition-all"
                >
                  Submit Another
                </button>
              </motion.div>
            ) : (
              <form
                data-testid={formType === "partner" ? "partner-inquiry-form" : "catalogue-form"}
                onSubmit={formType === "partner" ? handlePartnerSubmit : handleCatalogueSubmit}
                className="bg-[#FAFAF9] p-8 md:p-12"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      data-testid="input-name"
                      className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      name="company"
                      required
                      data-testid="input-company"
                      className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      data-testid="input-email"
                      className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      data-testid="input-phone"
                      className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                      placeholder="+1234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                    <input
                      type="text"
                      name="country"
                      required
                      data-testid="input-country"
                      className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                      placeholder="Your country"
                    />
                  </div>
                  {formType === "partner" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Category *</label>
                      <select
                        name="product_category"
                        required
                        data-testid="select-category"
                        className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all bg-white"
                      >
                        <option value="">Select category</option>
                        <option value="food-fmcg">Food & FMCG</option>
                        <option value="electronics">Electrical & Electronics</option>
                        <option value="industrial">Industrial & Automation</option>
                        <option value="furniture">Furniture & Interiors</option>
                        <option value="renewable">Renewable Energy</option>
                        <option value="automotive">Automotive & Accessories</option>
                        <option value="building">Building Materials & Hardware</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formType === "partner" ? "Message / Product Details *" : "Product Description *"}
                  </label>
                  <textarea
                    name={formType === "partner" ? "message" : "product_description"}
                    required
                    rows={5}
                    data-testid="textarea-message"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow resize-none"
                    placeholder={formType === "partner" ? "Tell us about your products and partnership interest..." : "Describe your products and what you're looking to distribute..."}
                  />
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Catalogue (PDF, up to 10MB)
                  </label>
                  <div className="border-2 border-dashed border-gray-200 p-6 text-center hover:border-[#043927] transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      data-testid="input-file"
                      className="hidden"
                      id="catalogue-upload"
                    />
                    <label htmlFor="catalogue-upload" className="cursor-pointer">
                      <Upload size={32} className="mx-auto mb-3 text-gray-400" />
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText size={18} className="text-[#043927]" />
                          <span className="text-[#043927] font-medium">{selectedFile.name}</span>
                        </div>
                      ) : (
                        <>
                          <p className="text-gray-600 mb-1">Click to upload or drag and drop</p>
                          <p className="text-sm text-gray-400">PDF, DOC, DOCX (max 10MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-button"
                  className="w-full bg-[#043927] text-white py-4 text-lg font-semibold hover:bg-[#01150E] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      {formType === "partner" ? "Submit Partnership Inquiry" : "Send Catalogue"}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Alternative Contact */}
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">Or reach us directly</p>
              <div className="flex flex-wrap justify-center gap-6">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex items-center gap-2 text-[#043927] hover:text-[#D4AF37] transition-colors"
                >
                  <Mail size={18} />
                  {CONTACT_EMAIL}
                </a>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#25D366] hover:text-[#128C7E] transition-colors"
                >
                  <ExternalLink size={18} />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    const formData = new FormData(e.target);
    const data = {
      name: formData.get("name"),
      company: formData.get("company"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      message: formData.get("message")
    };

    try {
      await axios.post(`${API}/contact`, data);
      setSubmitSuccess(true);
      e.target.reset();
    } catch (error) {
      setSubmitError("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-[#043927]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 max-w-3xl">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Have questions? Ready to explore partnership opportunities? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#043927] mb-8">
                Contact Information
              </h2>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#043927] rounded flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#043927] mb-1">Email</h4>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-gray-600 hover:text-[#D4AF37]">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#043927] rounded flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#043927] mb-1">Landline</h4>
                    <a href={`tel:${LANDLINE_NUMBER.replace(/\s/g, '')}`} className="text-gray-600 hover:text-[#D4AF37]">
                      {LANDLINE_NUMBER}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#043927] rounded flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#043927] mb-1">WhatsApp</h4>
                    <a href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#D4AF37]">
                      {WHATSAPP_NUMBER}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#043927] rounded flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#043927] mb-1">Business Hours</h4>
                    <p className="text-gray-600">Sunday - Thursday: 9:00 AM - 6:00 PM</p>
                    <p className="text-gray-600">Saturday: 9:00 AM - 3:00 PM</p>
                    <p className="text-gray-600">Friday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12">
                <div className="w-full h-[300px] bg-gray-100 overflow-hidden map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d462560.30105432544!2d54.89786478247958!3d25.07628045498634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e5084295162!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2s!4v1699999999999!5m2!1sen!2s"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Market Plus Location"
                  />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16 bg-[#043927] text-white form-success h-full flex flex-col items-center justify-center"
                >
                  <CheckCircle size={64} className="mb-6 text-[#D4AF37]" />
                  <h3 className="text-2xl font-bold mb-4">Message Sent!</h3>
                  <p className="text-gray-300 mb-8">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="bg-[#D4AF37] text-[#01150E] px-8 py-3 font-semibold hover:bg-[#D4AF37]/90 transition-all"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="bg-[#FAFAF9] p-8 md:p-12"
                >
                  <h2 className="text-2xl font-bold text-[#043927] mb-8">
                    Send Us a Message
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        data-testid="contact-input-name"
                        className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        name="company"
                        required
                        data-testid="contact-input-company"
                        className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        data-testid="contact-input-email"
                        className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        data-testid="contact-input-phone"
                        className="w-full h-12 px-4 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow"
                        placeholder="+1234567890"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        data-testid="contact-textarea-message"
                        className="w-full px-4 py-3 border border-gray-200 focus:border-[#043927] focus:ring-1 focus:ring-[#043927] outline-none transition-all input-glow resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    {submitError && (
                      <div className="p-4 bg-red-50 border border-red-200 text-red-600">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="w-full bg-[#043927] text-white py-4 text-lg font-semibold hover:bg-[#01150E] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/divisions" element={<DivisionsPage />} />
          <Route path="/partner" element={<PartnerPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
